import React, { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { NotificationForm } from "./NotificationForm";
import { StatusBadge, UrgencyBadge } from "./Badge";
import { SaveIcon, TrashIcon, RefreshIcon } from "./Icons";
import { useNotificationForm } from "../hooks/useNotificationForm";
import { formatDate, formatTargetAudience, formatPlatform, toEpochSeconds } from "../utils/formatters";

export const EditNotificationModal = ({
  isOpen,
  onClose,
  notification,
  onUpdate,
  onRepublish,
  onDelete,
  getAudienceLabel,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [wasSubmitted, setWasSubmitted] = useState(false);

  const {
    formData,
    updateField,
    togglePlatform,
    setUrgency,
    toggleSchedule,
    resetForm,
    characterCount,
    scheduleError,
  } = useNotificationForm(notification);

  const stopEditing = () => {
    setIsEditing(false);
    setWasSubmitted(false);
  };

  const handleClose = () => {
    stopEditing();
    resetForm();
    onClose();
  };

  const handleSaveChanges = () => {
    setWasSubmitted(true);
    if (scheduleError) return;
    onUpdate(notification.id, formData);
    stopEditing();
  };

  const handleSaveAndRepublish = () => {
    setWasSubmitted(true);
    if (scheduleError) return;
    onRepublish(notification.id, formData);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(notification);
    handleClose();
  };

  if (!notification) return null;

  const viewFooter = (
    <>
      <Button variant="danger" onClick={handleDelete} icon={<TrashIcon />} className="mr-auto">
        Delete
      </Button>
      <Button variant="secondary" onClick={handleClose}>
        Close
      </Button>
      <Button variant="primary" onClick={() => setIsEditing(true)}>
        Edit
      </Button>
    </>
  );

  const editFooter = (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={stopEditing}>
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleSaveChanges} icon={<SaveIcon />}>
            Save Changes
          </Button>
          <Button variant="primary" onClick={handleSaveAndRepublish} icon={<RefreshIcon />}>
            Save & Republish
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-2 text-right">
        Save & Republish also resends the notification to users.
      </p>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? "Edit Notification" : "Notification Details"}
      subtitle={isEditing ? "Edit Mode - Make your changes below" : null}
      footer={isEditing ? editFooter : viewFooter}
      footerClassName="bg-slate-50/80 border-t border-slate-200"
      headerClassName=""
      size="large"
    >
      {isEditing ? (
        <NotificationForm
          formData={formData}
          updateField={updateField}
          togglePlatform={togglePlatform}
          setUrgency={setUrgency}
          toggleSchedule={toggleSchedule}
          characterCount={characterCount}
          scheduleError={wasSubmitted ? scheduleError : null}
        />
      ) : (
        <div className="space-y-6">
          {/* Header with title and badges */}
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-lg font-semibold text-slate-800 leading-snug pr-4">
              {notification.title}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              <StatusBadge status={notification.status} />
              <UrgencyBadge urgency={notification.urgency} />
            </div>
          </div>

          {/* Message */}
          <div className="rounded-lg bg-slate-50/80 px-4 py-3.5">
            <p className="text-slate-600 text-[15px] leading-relaxed">
              {notification.notificationMessage}
            </p>
          </div>

          {/* Details in a compact card */}
          <div className="rounded-xl border border-slate-200/80 bg-white p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <DetailItem
                label="Target Audience"
                value={getAudienceLabel ? getAudienceLabel(notification.targetAudience) : formatTargetAudience(notification.targetAudience)}
              />
              <DetailItem label="Duration" value={`${notification.duration} min`} />
              <DetailItem label="Created By" value={notification.createdBy || "—"} />
              <DetailItem label="Created" value={formatDate(notification.createdAt)} />
              <DetailItem label="Scheduled" value={formatDate(notification.scheduledSendTime)} />
              <DetailItem label="Expires" value={formatDate(notification.expiryTime)} />
              <div className="col-span-2 sm:col-span-3 min-w-0">
                <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-0.5">Platforms</p>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {notification.platforms.length > 0 ? (
                    notification.platforms.map((platform) => (
                      <span
                        key={platform}
                        className="inline-flex items-center px-3 py-1.5 rounded-md bg-ktx-purple/10 text-ktx-purple text-sm font-medium"
                      >
                        {formatPlatform(platform)}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 text-sm">No platforms</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Schedule callout */}
          {notification.scheduledSendTime && (
            <div className="flex items-center gap-3 rounded-lg bg-barely-purple/60 px-4 py-3 border border-light-purple/50">
              <span className="text-ktx-purple text-sm font-medium">Scheduled</span>
              <span className="text-slate-600 text-sm">
                {new Date(toEpochSeconds(notification.scheduledSendTime) * 1000).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
    <p className="text-slate-700 text-sm break-words">{value}</p>
  </div>
);
